/* tslint:disable:max-line-length */
import { BrowserModule } from "@angular/platform-browser";
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  LOCALE_ID,
  NgModule,
} from "@angular/core";
import { BrowserAnimationsModule, NoopAnimationsModule } from "@angular/platform-browser/animations";
import {
  HTTP_INTERCEPTORS, HttpBackend,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ExtraOptions, RouterModule, Routes } from '@angular/router';
import { environment } from "src/environments/environment";
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";
import { ColorChromeModule } from "ngx-color/chrome";
import {
  RECAPTCHA_LANGUAGE,
  RECAPTCHA_SETTINGS,
  RecaptchaFormsModule,
  RecaptchaModule,
  RecaptchaSettings,
} from 'ng-recaptcha';
import { AppComponent } from './app.component';
import { CacheInterceptor } from './interceptors/cache.interceptor';
import { TokenInterceptor } from './interceptors/token.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { LocationInterceptor } from './interceptors/location.interceptor';
import { PrivateGuard } from './guards/private.guard';
import { AuthGuard } from './guards/auth.guard';
import { LoggerService } from './services/logger.service';
import { ConsoleLoggerService } from './services/console-logger.service';
import { InitService } from './services/init.service';
import { ApiService } from './services/api.service';
import { GlobalErrorHandler } from './error-handler';
import { HomeComponent } from './components/home/home.component';
import { LoginRemoteComponent } from './components/login/login-remote/login-remote.component';
import { LoginCallbackComponent } from './components/login/login-callback/login-callback.component';
import { IberiaLandingComponent } from './components/landings/iberia/iberia-landing.component';
import { CollectionsComponent } from './components/collections/collections.component';
import { InfoComponent } from './components/info/info.component';
import { UserComponent } from './components/user/user.component';
import { BaseButtonComponent } from './components/buttons/base-button';
import { RecordButtonsComponent } from './components/buttons/record-buttons/record-buttons.component';
import { CheckoutButtonComponent } from './components/buttons/checkout-button/checkout-button.component';
import { HoldButtonComponent } from './components/buttons/hold-button/hold-button.component';
import { ViewAllButtonComponent } from './components/buttons/view-all-button/view-all-button.component';
import { FavoriteButtonComponent } from './components/buttons/favorite-button/favorite-button.component';
import { LogoutComponent } from './components/logout/logout.component';
import { LogoutConfirmationComponent } from './components/logout-confirmation/logout-confirmation.component';
import { ResultsComponent } from './components/results/results.component';
import { ResultsItemComponent } from './components/results-item/results-item.component';
import { SearchBarComponent } from './components/search-bar/search-bar.component';
import { DownloadButtonComponent } from './components/buttons/download-button/download-button.component';
import { ReturnButtonComponent } from './components/buttons/return-button/return-button.component';
import { PreviewButtonComponent } from './components/buttons/preview-button/preview-button.component';
import { CancelHoldButtonComponent } from './components/buttons/cancel-hold-button/cancel-hold-button.component';
import { AddToListButtonComponent } from './components/buttons/add-to-list-button/add-to-list-button.component';
import { ExternalLinkButtonComponent } from './components/buttons/external-link-button/external-link-button.component';
import { CalendarButtonComponent } from './components/buttons/calendar-button/calendar-button.component';
import { RenewCheckoutButtonComponent } from './components/buttons/renew-checkout-button/renew-checkout-button.component';
import { RemoveButtonComponent } from './components/buttons/remove-button/remove-button.component';
import { DownloadAttachedResourceButtonComponent } from './components/buttons/download-attached-resource-button/download-attached-resource-button.component';
import { WaitingButtonComponent } from './components/buttons/waiting-button/waiting-button.component';
import { SliderComponent } from './components/slider/slider.component';
import { SliderItemInfoComponent } from './components/slider-item-info/slider-item-info.component';
import { SliderCollectionItemComponent } from './components/slider/slider-collection-item/slider-collection-item.component';
import { SliderListItemComponent } from './components/slider/slider-list-item/slider-list-item.component';
import { TermsConditionsDialogComponent } from './components/dialogs/terms-conditions-dialog/terms-conditions-dialog.component';
import { ViewAllValuesDialogComponent } from './components/view-all-values-dialog/view-all-values-dialog.component';
import { InterestsDialogComponent } from './components/interests-dialog/interests-dialog.component';
import { PurchaseSuggestionsDialogComponent } from './components/purchase-suggestions-dialog/purchase-suggestions-dialog.component';
import { TutorsDialogComponent } from './components/tutors-dialog/tutors-dialog.component';
import { UserStatsComponent } from './components/user/user-stats/user-stats.component';
import { UserSuggestionsComponent } from './components/user/user-suggestions/user-suggestions.component';
import { UserActiveDevicesComponent } from './components/user/user-active-devices/user-active-devices.component';
import { UserCheckoutsComponent } from './components/user/user-checkouts/user-checkouts.component';
import { UserCheckoutItemComponent } from './components/user/user-checkout-item/user-checkout-item.component';
import { UserCheckoutHistoryComponent } from './components/user/user-checkout-history/user-checkout-history.component';
import { UserCheckoutHistoryItemComponent } from './components/user/user-checkout-history-item/user-checkout-history-item.component';
import { UserHoldsComponent } from './components/user/user-holds/user-holds.component';
import { UserHoldItemComponent } from './components/user/user-hold-item/user-hold-item.component';
import { UserFavoritesComponent } from './components/user/user-favorites/user-favorites.component';
import { UserFavoriteItemComponent } from './components/user/user-favorite-item/user-favorite-item.component';
import { UserListsComponent } from './components/user/user-lists/user-lists.component';
import { UserSearchesComponent } from './components/user/user-searches/user-searches.component';
import { UserSearchTableComponent } from './components/user/user-search-table/user-search-table.component';
import { UserListsItemComponent } from './components/user/user-lists-item/user-lists-item.component';
import { UserBookClubsComponent } from './components/user/user-book-clubs/user-book-clubs.component';
import { UserInterestsComponent } from './components/user/user-interests/user-interests.component';
import { UserVerifyAccountComponent } from './components/user/user-verify-email/user-verify-account.component';
import { RadioQuestionComponent } from './components/user/user-interests/radio-question/radio-question.component';
import { TagQuestionComponent } from './components/user/user-interests/tag-question/tag-question.component';
import { SliderBookClubsComponent } from './components/slider-book-clubs/slider-book-clubs.component';
import { PublicListComponent } from './components/public-list/public-list.component';
import { FiltersComponent } from './components/filters/filters.component';
import { FormatFilterComponent } from './components/format-filter/format-filter.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { FooterComponent } from './components/footer/footer.component';
import { BannerComponent } from './components/banner/banner.component';
import { TagCloudComponent } from './components/tag-cloud/tag-cloud.component';
import { SearchTermsComponent } from './components/search-terms/search-terms.component';
import { UserPictureComponent } from './components/user/user-picture/user-picture.component';
import { ScrollToTopComponent } from './components/scroll-to-top/scroll-to-top.component';
import { WelcomeComponent } from './components/welcome/welcome.component';
import { ImageGalleryComponent } from './components/image-gallery/image-gallery.component';
import { TopicSliderComponent } from './components/topic-slider/topic-slider.component';
import { TopicsComponent } from './components/topics/topics.component';
import { RatingBlockComponent } from './components/rating-block/rating-block.component';
import { PurchaseSuggestionsItemComponent } from './components/purchase-suggestions-item/purchase-suggestions-item.component';
import { ThemeDesignerComponent } from './components/theme-designer/theme-designer.component';
import { SvgImageComponent } from './components/svg-image/svg-image.component';
import { SmartBannerComponent } from './components/smart-banner/smart-banner.component';
import { PreLoadDirective } from './directives/pre-load.directive';
import { RecordTitleDirective } from './directives/record-title.directive';
import { ClickStopPropagationDirective } from './directives/click-stop-propagation.directive';
import { FileValueAccessorDirective } from './components/login/input-types/file-value-accessor.directive';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { RemoveHtmlPipe } from './pipes/remove-html.pipe';
import { ToBadgePipe } from './pipes/to-badge.pipe';
import { ParseDatePipe } from './pipes/parse-date.pipe';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { RemoveQuotesPipe } from './pipes/remove-quotes.pipe';
import { FormlyFieldFileComponent } from './components/login/input-types/file-type.component';
import { FormlyFieldDatePickerComponent } from './components/login/input-types/datepicker-type.component';
import { FormlyFieldCustomSelectComponent } from './components/login/input-types/custom-select-type.component';
import { TutorsFormComponent } from './components/tutors-form/tutors-form.component';
import { InvitationsFormComponent } from './components/invitations-form/invitations-form.component';
import { UserVisualizationHistoryComponent } from './components/user/user-visualization-history/user-visualization-history.component';
import { UserVisualizationsHistoryItemComponent } from './components/user/user-visualization-history-item/user-visualization-history-item.component';
import { BlankComponent } from './components/blank/blank.component';
import { CookiePolicyComponent } from './components/cookie-policy/cookie-policy.component';
import { CustomMissingTranslationHandler } from './custom-missing-translation-handler.service';
import { AcceptHoldCallbackComponent } from './components/accept-hold-callback/accept-hold-callback.component';
import { AssistantModule } from './assistant/assitant.module';
import { ZendeskConfig } from './models/client-configuration';
import { UserStatsTableHeaderComponent } from './components/user/user-stats/user-stats-tables/user-stats-table-header/user-stats-table-header.component';
import { UserStatsTableRowComponent } from './components/user/user-stats/user-stats-tables/user-stats-table-row/user-stats-table-row.component';
import { ResourceTypeSelectorComponent } from './components/resource-type-selector/resource-type-selector.component';
import { UserStatsLastUsageTableComponent } from './components/user/user-stats/user-stats-tables/user-stats-last-usage-table/user-stats-last-usage-table.component';
import { StatisticsResolverService } from './services/resolvers/statistics-resolver.service';
import { UserStatsTotalTableComponent } from './components/user/user-stats/user-stats-tables/user-stats-total-table/user-stats-total-table.component';

import { ChallengeComponent } from './components/challenge/challenge.component';
import { UserChallengesResolverService } from './services/resolvers/user-challenges-resolver.service';
import { TranslateByCustomerTypePipe } from './pipes/translate-by-customer-type.pipe';

import { YoutubeResultsDialogComponent } from './components/youtube-results/youtube-results-dialog.component';
import { YoutubeItemComponent } from './components/youtube-item/youtube-item.component';

import { RadioQuestionLevelComponent } from './components/user/user-interests/radio-question/radio-question-level/radio-question-level.component';
import { UserResponsesResolverService } from './services/resolvers/user-responses-resolver.service';
import { DevicesGuard } from './guards/devices.guard';
import { VisualizeImageButtonComponent } from './components/buttons/visualize-image-button/visualize-image-button.component';
import { SliderFavouritesComponent } from './components/slider-favourites/slider-favourites.component';
import { SliderFavouritesItemComponent } from './components/slider/slider-favourites-item/slider-favourites-item.component';
import { SliderSubheaderDirective } from './directives/slider-subheader.directive';
import { SliderActionsDirective } from './directives/slider-actions.directive';
import { SliderClassicComponent } from './components/slider-classic/slider-classic.component';
import { SliderListComponent } from './components/slider-list/slider-list.component';
import { FavouritesComponent } from './components/favourites/favourites.component';
import { SliderAuthorsComponent } from './components/slider-authors/slider-authors.component';
import { SliderAuthorsItemComponent } from './components/slider-authors/slider-authors-item/slider-authors-item.component';
import { AuthorsComponent } from './components/authors/authors.component';
import { AuthorCardBgSvgComponent } from './components/author-card-bg-svg/author-card-bg-svg.component';
import { ValidateButtonComponent } from './components/buttons/validate-button/validate-button.component';
import { UserCreateGuestAccountComponent } from './components/user/user-create-guest-account/user-create-guest-account.component';
import { HoldsGuard } from './guards/holds.guard';
import { RecordFacadeComponent } from './components/record-facade/record-facade.component';
import { ContainerRecordItemsComponent } from './components/container-record-items/container-record-items.component';
import { SliderUserActionsComponent } from './components/slider-user-actions/slider-user-actions.component';
import { UserActionsBookClubsComponent } from './components/user/user-actions-book-clubs/user-actions-book-clubs.component';
import { RssAllItemsComponent } from './components/rss-all-items/rss-all-items.component';
import { RecordInteractionsComponent } from './components/record-interactions/record-interactions.component';
import { RecordSliderComponent } from './components/record-slider/record-slider.component';
import { SliderTitleDirective } from './directives/slider-title.directive';
import { UserHistoryComponent } from './components/user/user-history/user-history.component';
import { UserHistoryItemComponent } from './components/user/user-history-item/user-history-item.component';
import { PlaceholderSwiperComponent } from './components/placeholder-swiper/placeholder-swiper.component';
import { RecordCardGridComponent } from './components/record-card-grid/record-card-grid.component';
import { RecordCardGenericComponent } from './components/record-card-generic/record-card-generic.component';
import { RecordCardGenericLeftDirective } from './directives/record-card-generic-left.directive';
import { RecordCardGenericRightDirective } from './directives/record-card-generic-right.directive';
import { RssItemComponent } from './components/rss-item/rss-item.component';
import { ListsItemComponent } from './components/lists-item/lists-item.component';
import { CitationButtonComponent } from './components/buttons/citation-button/citation-button.component';
import { CasComponent } from './components/cas/cas.component';
import { CertificateButtonComponent } from './components/buttons/certificate-button/certificate-button.component';
import { MoreOptionButtonComponent } from './components/buttons/more-option-button/more-option-button.component';
import { PhysicalRecordsTableComponent } from './components/physical-records-table/physical-records-table.component';
import { FascicleRecordsTableComponent } from './components/fascicle-records-table/fascicle-records-table.component';
import { HoldPhysicalButtonComponent } from './components/buttons/hold-physical-button/hold-physical-button.component';
import { CancelHoldPhysicalButtonComponent } from './components/buttons/cancel-hold-physical-button/cancel-hold-physical-button.component';
import { UserSuggestionsCardComponent } from './components/user-suggestions-card/user-suggestions-card.component';
import { UserSuggestionsItemComponent } from './components/user/user-suggestions-item/user-suggestions-item.component';
import { CardItemComponent } from './components/card-item/card-item.component';
import { ButtonPanelDirective } from './directives/button-panel.directive';
import { RecordButtonsDirective } from './directives/record-buttons.directive';
import { UserEventsComponent } from './components/user/user-events/user-events.component';
import { UserEventsService } from './services/user-events/user-events.service';
import { InterestedUserService } from './services/interested-user.service';
import { ResourceResolver } from './services/resolvers/resource-resolver.service';
import { UserEventItemComponent } from './components/user/user-events/components/user-event-item/user-event-item.component';
import { PlatformsCalendarDialogComponent } from './components/dialogs/platforms-calendar-dialog/platforms-calendar-dialog.component';
import { EventsGuard } from './guards/events.guard';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material-legacy';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { GalleryModule } from '@ks89/angular-modal-gallery';

import { NgxZendeskWebwidgetModule } from '@nitsanzo/ngx-zendesk-webwidget';
import { NgChartsModule } from 'ng2-charts';
import { CareerPlansComponent } from './components/user/career-plans/career-plans.component';
import { SkillCardComponent } from './components/user/career-plans/skill-card/skill-card.component';
import { CircleArrowButtonComponent } from './components/buttons/circle-arrow-button/circle-arrow-button.component';
import { LevelCardComponent } from './components/user/career-plans/level-card/level-card.component';
import { ResourceCardComponent } from './components/user/career-plans/resource-card/resource-card.component';
import { ResourceSeparatorComponent } from './components/user/career-plans/resource-separator/resource-separator.component';
import { SkillDetailComponent } from './components/user/career-plans/skill-detail/skill-detail.component';
import { CareerPlanPositionComponent } from './components/user/career-plans/career-plan-position/career-plan-position.component';
import { SkillsRowComponent } from './components/user/career-plans/skills-row/skills-row.component';
import { CareerPlansApiService } from './services/api/career-plans-api.service';
import { LevelProgressComponent } from './components/user/career-plans/level-progress/level-progress.component';
import { CareerPlanHistoryComponent } from './components/user/career-plans/career-plan-history/career-plan-history.component';
import { CareerPlanHistoryItemComponent } from './components/user/career-plans/career-plan-history/components/career-plan-history-item.component';
import { ResourceCarouselComponent } from './components/user/career-plans/resources-carousel/resource-carousel.component';
import { CareerPlanHistoryService } from './components/user/career-plans/career-plan-history/career-plan-history.service';
import { InternalPageButtonComponent } from './components/buttons/internal-page-button/internal-page-button.component';
import { RecordGuard } from './guards/record.guard';
import { BookclubHistoryApiService } from './services/api/bookclub-history-api.service';
import { FollowButtonComponent } from './components/buttons/follow-button/follow-button.component';
import { RecordPaginationComponent } from './components/record-pagination/record-pagination.component';
import { CareerPlansGuard } from './guards/career-plans.guard';
import { UserHistoryService } from './components/user/user-history/service/user-history.service';
import { RecommendPlatformDialog } from './components/dialogs/recommend-platform-dialog/recommend-platform-dialog.component';
import { ActiveRecordsSwiperComponent } from './components/active-records-swiper/active-records-swiper.component';
import { ActiveRecordsApiService } from './services/api/active-records-api.service';
import { SliderViewMoreButtonComponent } from './components/buttons/slider-view-more-button/slider-view-more-button.component';
import { SocialButtonsComponent } from './components/social-buttons/social-buttons.component';
import { SocialButtonComponent } from './components/social-button/social-button.component';
import { IconButtonComponent } from './components/icon-button/icon-button.component';
import { MtnLandingComponent } from './components/landings/mtn/mtn-landing.component';
import { MtnApiService } from './services/api/mtn/mtn.service';
import { ExportButtonComponent } from './components/buttons/export-button/export-button.component';
import { ExportFormatsApiService } from './services/api/export-formats.service';
import { ShareButtonComponent } from './components/share/share-button/share-button.component';
import { ShareMenuComponent } from './components/share/share-menu/share-menu.component';
import { CareerPlanHistoryItemResolverService } from "./services/resolvers/career-plan-history-item-resolver.service";

/* tslint:enable:max-line-length */
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { stateInitializationModule } from './state';
import { CoreModule } from './_core/core.module';
import { ChallengesModule } from './challenges/challenges.module';
import { SharedModule } from './_shared/shared.module';
import { HomeChallengeComponent } from './components/home/components/home-challenge/home-challenge.component';
import { MaterialModule } from './_material/material.module';
import { UserConfigFacade } from './state/user-config/user-config.facade';
import { HeaderComponent } from './components/header/header.component';
import { HeaderUserButtonComponent } from './components/header/components/header-user-button/header-user-button.component';
import { HeaderBannersComponent } from './components/header/components/header-banners/header-banners.component';
import { HeaderNotificationsButtonComponent } from './components/header/components/header-notifications-button/header-notifications-button.component';
import { HeaderEventsButtonComponent } from './components/header/components/header-events-button/header-events-button.component';
import { HeaderHelpButtonComponent } from './components/header/components/header-help-button/header-help-button.component';
import { SwiperMoreButtonComponent } from './components/opac-swiper/components/swiper-more-button/swiper-more-button.component';
import { BaseDropdownOptionsButtonComponent } from './components/base-dropdown-options-button/base-dropdown-options-button.component';
import { EventSkeletonComponent } from './components/user/user-events/components/event-skeleton/event-skeleton.component';
import { ActiveRecordsCtaComponent } from "./components/active-records-swiper/components/active-records-cta/active-records-cta.component";
import { ActiveRecordsSkeletonComponent } from "./components/active-records-swiper/components/active-records-skeleton/active-records-skeleton.component";
import { ListsSkeletonComponent } from "./components/user/user-lists/components/lists-skeleton/lists-skeleton.component";
import { RatingComponent } from './components/rating/rating.component';
import { HeaderAlertBannerComponent } from './components/header/components/header-alert-banner/header-alert-banner.component';
import { AlertBannerBackgroundSvgComponent } from './components/header/components/header-alert-banner/components/alert-banner-background-svg/alert-banner-background-svg.component';
import { CardItemSkeletonComponent } from "./components/card-item/components/card-item-skeleton/card-item-skeleton.component";
import { PlayerUrlAnnotationService } from "./services/api/player-url-annotation.service";
import { PasswordInputComponent } from "./components/login/input-types/password/password-input.component";
import { InternalLoginModule } from "./internal-login/internal-login.module";
import { PhysicalLowerLevelTableComponent } from './components/physical-lower-level-table/physical-lower-level-table.component';
import { ComplementaryResourceCardComponent } from "./components/user/career-plans/complementary-resource-card/complementary-resource-card.component";
import { LanguageCodeToNamePipe } from './components/footer/pipes/language-code-to-name.pipe';
import { UserMenuComponent } from './components/user-menu/user-menu.component';
import { MetadataInfoComponent } from "./components/info/components/metadata-info.component";
import { editingGuard } from './_core/guards/editing.guard';
import { guestGuard } from "./_core/guards/guest.guard";
import { AulaMadridLandingComponent } from './components/aula-madrid-landing/aula-madrid-landing.component';
import { GamificationModule } from "./gamification/gamification.module";
import { ModalHandlerComponent } from './components/modal-handler/modal-handler.component';
import { EditListModule } from './edit-list/edit-list.module';
import { LoginModule } from './login/login.module';
import { InfoModule } from './info/info.module';
import { UserHistoryStatsDialogComponent } from './components/user/user-history/components/user-history-stats-dialog/user-history-stats-dialog.component';
import { UserHistoryStatsDialogItemComponent } from './components/user/user-history/components/user-history-stats-dialog-item/user-history-stats-dialog-item.component';
import { UserHistoryStatsDialogSkeletonComponent } from './components/user/user-history/components/user-history-stats-dialog-skeleton/user-history-stats-dialog-skeleton.component';

const appRoutes: Routes = [
  { path: 'redirectToCasLogin', component: CasComponent },
  { path: 'landing', component: IberiaLandingComponent },
  { path: 'globalgraduates', component: MtnLandingComponent },
  {
    path: 'aula-madrid',
    canActivate: [guestGuard],
    children: [
      {
        path: '',
        component: AulaMadridLandingComponent,
        data: { page: 'home' },
      },
      {
        path: 'ayuda',
        component: AulaMadridLandingComponent,
        data: { page: 'ayuda' },
      },
      {
        path: 'institucion',
        component: AulaMadridLandingComponent,
        data: { page: 'institucion' },
      },
    ],
  },
  { path: 'blank', component: BlankComponent },
  { path: 'cookie-policy', component: CookiePolicyComponent },
  { path: 'logout-confirmation', component: LogoutConfirmationComponent },
  {
    path: 'login',
    children: [
      { path: '', redirectTo: '/', pathMatch: 'full' },
      { path: 'remote', component: LoginRemoteComponent },
      { path: 'callback', component: LoginCallbackComponent },
    ],
  },
  {
    path: 'signup',
    loadChildren: () => import('./signup/signup.module').then(m => m.SignupModule),
  },
  {
    path: 'verify-account',
    loadChildren: () => import('./verify-account/verify-account.module').then(m => m.VerifyAccountModule),
  },
  {
    path: 'create-guest-account/:token',
    component: UserCreateGuestAccountComponent,
  },
  {
    path: '',
    canActivate: [PrivateGuard],
    canActivateChild: [DevicesGuard],
    children: [
      { path: '', component: HomeComponent },
      {
        path: 'info/:slug',
        component: InfoComponent,
        resolve: { recordResolved: ResourceResolver },
        canActivate: [RecordGuard],
        canDeactivate: [editingGuard],
      },
      { path: 'logout', component: LogoutComponent, canActivate: [AuthGuard] },
      {
        path: 'user',
        component: UserComponent,
        canActivate: [AuthGuard],
        children: [
          {
            path: 'account',
            loadChildren: () => import('./account/account.module').then(m => m.AccountModule),
          },
          { path: 'details', redirectTo: '/user/account' },
          { path: 'career-plans', component: CareerPlansComponent },
          {
            path: 'career-plans/:careerPlanId',
            component: CareerPlanHistoryItemComponent,
            resolve: { careerPlan: CareerPlanHistoryItemResolverService },
          },
          { path: 'checkouts', component: UserCheckoutsComponent },
          {
            path: 'checkouts/history',
            component: UserCheckoutHistoryComponent,
          },
          {
            path: 'holds',
            component: UserHoldsComponent,
            canActivate: [HoldsGuard],
          },
          { path: 'favorites', component: UserFavoritesComponent },
          {
            path: 'notifications',
            loadChildren: () => import('./notifications/notifications.module').then(m => m.NotificationsModule),
          },
          { path: 'devices', component: UserActiveDevicesComponent },
          { path: 'lists', component: UserListsComponent },
          {
            path: 'interests',
            component: UserInterestsComponent,
            resolve: { userInterests: UserResponsesResolverService },
          },
          { path: 'search', component: UserSearchesComponent },
          {
            path: 'challenges',
            loadChildren: () => ChallengesModule,
          },
          {
            path: 'gamification',
            loadChildren: () => GamificationModule,
          },
          {
            path: 'stats',
            component: UserStatsComponent,
            resolve: { userStatsBundle: StatisticsResolverService },
          },
          {
            path: 'suggestions',
            component: UserSuggestionsComponent,
          },
          {
            path: 'desiderata',
            loadChildren: () => import('./desiderata/desiderata.module').then(m => m.DesiderataModule),
          },
          {
            path: 'visualizations',
            component: UserVisualizationHistoryComponent,
          },
          { path: 'experiences', component: UserBookClubsComponent },
          { path: 'history', component: UserHistoryComponent },
          {
            path: 'events',
            component: UserEventsComponent,
            canActivate: [EventsGuard],
          },
        ],
      },
      { path: 'results', component: ResultsComponent },
      { path: 'list/:slug', component: PublicListComponent },
      { path: 'rss/:slug', component: RssAllItemsComponent },
      {
        path: 'favourites',
        component: FavouritesComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'authors',
        component: AuthorsComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'search',
        loadChildren: () => import('./search/search.module').then(m => m.SearchModule),
        canActivate: [AuthGuard],
      },
      { path: 'topics', component: TopicsComponent },
      { path: 'db.reserve', component: AcceptHoldCallbackComponent },
      { path: '**', redirectTo: '' },
    ],
  },
];

const routesOptions: ExtraOptions = {
  enableTracing: false,
  scrollPositionRestoration: 'top'
};

export function getInitConfig(initService: InitService, userConfig: UserConfigFacade) {
  return () => {
    return initService.getConfiguration().then(() => {
      return userConfig.getUserConfig();
    });
  };
}

export function createTranslateLoader(http: HttpBackend) {
  return new MultiTranslateHttpLoader(new HttpClient(http), [
    { prefix: './assets/i18n/', suffix: '.json' },
    { prefix: './assets/i18n/countries/', suffix: '.json' },
    { prefix: './assets/i18n/languages/', suffix: '.json' },
  ]);
}

@NgModule({
  declarations: [
    AppComponent,
    LoginRemoteComponent,
    LoginCallbackComponent,
    CollectionsComponent,
    InfoComponent,
    UserComponent,
    BaseButtonComponent,
    RecordButtonsComponent,
    LogoutComponent,
    LogoutConfirmationComponent,
    ResultsComponent,
    SearchBarComponent,
    CheckoutButtonComponent,
    FollowButtonComponent,
    HoldButtonComponent,
    HoldPhysicalButtonComponent,
    FavoriteButtonComponent,
    DownloadButtonComponent,
    ReturnButtonComponent,
    PreviewButtonComponent,
    CancelHoldButtonComponent,
    CancelHoldPhysicalButtonComponent,
    ExternalLinkButtonComponent,
    CalendarButtonComponent,
    RenewCheckoutButtonComponent,
    RemoveButtonComponent,
    CertificateButtonComponent,
    MoreOptionButtonComponent,
    DownloadAttachedResourceButtonComponent,
    WaitingButtonComponent,
    SliderComponent,
    ViewAllValuesDialogComponent,
    SafeHtmlPipe,
    ParseDatePipe,
    RemoveHtmlPipe,
    ToBadgePipe,
    RemoveQuotesPipe,
    UserStatsComponent,
    UserSuggestionsComponent,
    UserActiveDevicesComponent,
    UserCheckoutsComponent,
    UserHoldsComponent,
    UserFavoritesComponent,
    UserListsComponent,
    UserInterestsComponent,
    UserSearchesComponent,
    UserSearchTableComponent,
    UserVerifyAccountComponent,
    UserCreateGuestAccountComponent,
    AddToListButtonComponent,
    PublicListComponent,
    FiltersComponent,
    ResultsItemComponent,
    SidebarComponent,
    FooterComponent,
    FormatFilterComponent,
    TermsConditionsDialogComponent,
    BannerComponent,
    HomeComponent,
    InterestsDialogComponent,
    RecommendPlatformDialog,
    TagCloudComponent,
    SearchTermsComponent,
    SliderItemInfoComponent,
    SliderCollectionItemComponent,
    SliderListItemComponent,
    SmartBannerComponent,
    UserCheckoutItemComponent,
    PreLoadDirective,
    RecordTitleDirective,
    ClickStopPropagationDirective,
    FileValueAccessorDirective,
    UserHoldItemComponent,
    UserCheckoutHistoryComponent,
    UserCheckoutHistoryItemComponent,
    UserFavoriteItemComponent,
    UserPictureComponent,
    UserListsItemComponent,
    ScrollToTopComponent,
    WelcomeComponent,
    ImageGalleryComponent,
    TopicSliderComponent,
    TopicsComponent,
    RatingBlockComponent,
    PurchaseSuggestionsDialogComponent,
    PurchaseSuggestionsItemComponent,
    ThemeDesignerComponent,
    SvgImageComponent,
    FormlyFieldFileComponent,
    FormlyFieldDatePickerComponent,
    FormlyFieldCustomSelectComponent,
    TutorsDialogComponent,
    TutorsFormComponent,
    InvitationsFormComponent,
    UserVisualizationHistoryComponent,
    UserVisualizationsHistoryItemComponent,
    BlankComponent,
    CookiePolicyComponent,
    LogoutConfirmationComponent,
    AcceptHoldCallbackComponent,
    SliderBookClubsComponent,
    UserBookClubsComponent,
    IberiaLandingComponent,
    MtnLandingComponent,
    ResourceTypeSelectorComponent,
    UserStatsTableHeaderComponent,
    UserStatsTableRowComponent,
    UserStatsLastUsageTableComponent,
    UserStatsTotalTableComponent,
    RadioQuestionComponent,
    RadioQuestionLevelComponent,
    TagQuestionComponent,
    ChallengeComponent,
    TranslateByCustomerTypePipe,
    YoutubeResultsDialogComponent,
    YoutubeItemComponent,
    VisualizeImageButtonComponent,
    SliderFavouritesComponent,
    SliderFavouritesItemComponent,
    SliderSubheaderDirective,
    SliderActionsDirective,
    SliderClassicComponent,
    SliderListComponent,
    FavouritesComponent,
    SliderAuthorsComponent,
    SliderAuthorsComponent,
    SliderAuthorsItemComponent,
    AuthorsComponent,
    AuthorCardBgSvgComponent,
    ValidateButtonComponent,
    RecordFacadeComponent,
    ContainerRecordItemsComponent,
    ViewAllButtonComponent,
    SliderUserActionsComponent,
    UserActionsBookClubsComponent,
    RecordFacadeComponent,
    ContainerRecordItemsComponent,
    ViewAllButtonComponent,
    RssAllItemsComponent,
    RecordInteractionsComponent,
    RecordPaginationComponent,
    RecordSliderComponent,
    SliderTitleDirective,
    UserHistoryComponent,
    UserHistoryItemComponent,
    PlaceholderSwiperComponent,
    RecordCardGridComponent,
    RecordCardGenericComponent,
    RecordCardGenericLeftDirective,
    RecordCardGenericRightDirective,
    RssItemComponent,
    ListsItemComponent,
    SliderTitleDirective,
    RecordInteractionsComponent,
    RecordPaginationComponent,
    ListsItemComponent,
    CitationButtonComponent,
    CasComponent,
    PhysicalRecordsTableComponent,
    FascicleRecordsTableComponent,
    UserSuggestionsCardComponent,
    UserSuggestionsItemComponent,
    CardItemComponent,
    ButtonPanelDirective,
    RecordButtonsDirective,
    UserEventsComponent,
    UserEventItemComponent,
    PlatformsCalendarDialogComponent,
    CareerPlansComponent,
    SkillCardComponent,
    CircleArrowButtonComponent,
    LevelCardComponent,
    ResourceCardComponent,
    ResourceSeparatorComponent,
    SkillDetailComponent,
    CareerPlanPositionComponent,
    SkillsRowComponent,
    LevelProgressComponent,
    CareerPlanHistoryComponent,
    CareerPlanHistoryItemComponent,
    ResourceCarouselComponent,
    InternalPageButtonComponent,
    SliderViewMoreButtonComponent,
    ActiveRecordsSwiperComponent,
    ActiveRecordsCtaComponent,
    ActiveRecordsSkeletonComponent,
    SocialButtonsComponent,
    SocialButtonComponent,
    IconButtonComponent,
    ExportButtonComponent,
    ShareButtonComponent,
    ShareMenuComponent,
    HomeChallengeComponent,
    HeaderComponent,
    HeaderUserButtonComponent,
    HeaderBannersComponent,
    HeaderNotificationsButtonComponent,
    HeaderEventsButtonComponent,
    HeaderHelpButtonComponent,
    SwiperMoreButtonComponent,
    BaseDropdownOptionsButtonComponent,
    EventSkeletonComponent,
    ListsSkeletonComponent,
    RatingComponent,
    CardItemSkeletonComponent,
    HeaderAlertBannerComponent,
    AlertBannerBackgroundSvgComponent,
    PasswordInputComponent,
    PhysicalLowerLevelTableComponent,
    ComplementaryResourceCardComponent,
    LanguageCodeToNamePipe,
    UserMenuComponent,
    MetadataInfoComponent,
    AulaMadridLandingComponent,
    ModalHandlerComponent,
    UserHistoryStatsDialogComponent,
    UserHistoryStatsDialogItemComponent,
    UserHistoryStatsDialogSkeletonComponent,
  ],
  imports: [
    CommonModule,
    CoreModule,
    NoopAnimationsModule,
    RouterModule.forRoot(appRoutes, routesOptions),
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    MaterialModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpBackend],
      },
    }),
    GalleryModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule.forRoot({
      types: [
        {
          name: 'file',
          component: FormlyFieldFileComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'datepicker',
          component: FormlyFieldDatePickerComponent,
          wrappers: ['form-field'],
          defaultOptions: {
            props: {
              datepickerOptions: {},
            },
          },
        },
        {
          name: 'custom-select',
          component: FormlyFieldCustomSelectComponent,
          wrappers: ['form-field'],
        },
        {
          name: 'custom-password',
          component: PasswordInputComponent
        },
      ],
    }),
    FormlyMaterialModule,
    ColorChromeModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    AssistantModule,
    NgxZendeskWebwidgetModule.forRoot(ZendeskConfig),
    FontAwesomeModule,
    NgChartsModule,
    stateInitializationModule,
    NgxsReduxDevtoolsPluginModule.forRoot(),
    SharedModule,
    InternalLoginModule,
    EditListModule,
    LoginModule,
    InfoModule,
  ],
  providers: [
    DatePipe,
    DecimalPipe,
    InitService,
    { provide: LoggerService, useClass: ConsoleLoggerService },
    {
      provide: APP_INITIALIZER,
      useFactory: getInitConfig,
      deps: [InitService, UserConfigFacade],
      multi: true,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },

    AuthGuard,
    DevicesGuard,
    HoldsGuard,
    EventsGuard,
    CareerPlansGuard,
    ApiService,
    CareerPlansApiService,
    BookclubHistoryApiService,
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LocationInterceptor, multi: true },
    {
      provide: LOCALE_ID,
      useFactory: (t: TranslateService) => {
        // Obtenemos el idioma cargado con ngx-translate pero mapeamos el valenciano con el soportado por Angular
        return t.currentLang === 'va' ? 'ca-ES-valencia' : t.currentLang;
      },
      deps: [TranslateService],
    },
    {
      provide: RECAPTCHA_LANGUAGE,
      useFactory: (t: TranslateService) => {
        // Obtenemos el idioma cargado con ngx-translate pero mapeamos el valenciano con el soportado por el captcha
        return t.currentLang === 'va' ? 'ca' : t.currentLang;
      },
      deps: [TranslateService],
    },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: { siteKey: environment.reCaptchaKey } as RecaptchaSettings,
    },
    {
      provide: MissingTranslationHandler,
      useClass: CustomMissingTranslationHandler,
    },
    StatisticsResolverService,
    UserChallengesResolverService,
    UserResponsesResolverService,
    InterestedUserService,
    UserEventsService,
    CareerPlanHistoryService,
    RecordGuard,
    UserHistoryService,
    ActiveRecordsApiService,
    ExportFormatsApiService,
    MtnApiService,
    PlayerUrlAnnotationService,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  exports: [ActiveRecordsSwiperComponent, SafeHtmlPipe],
  bootstrap: [AppComponent],
})
export class AppModule { }
